import { useEffect, useState } from 'react';
import PhotoGallery from './PhotoGallery';
import PhotoUpload from './PhotoUpload';
import { Photo } from '../../api/photos';
import { deletePhoto, updatePhotoCaption, uploadBirdPhoto } from '../../api/photos';

interface BirdPhotosProps {
  birdId: string;
  initialPhotos: Photo[];
  onAvatarChange?: (photoId: string) => Promise<void>;
  onPhotosChange?: (photos: Photo[]) => void;
  selectedAvatar?: string;
}

export default function BirdPhotos({
  birdId,
  initialPhotos,
  onAvatarChange,
  onPhotosChange,
  selectedAvatar
}: BirdPhotosProps) {
  const [photos, setPhotos] = useState<Photo[]>(initialPhotos);

  useEffect(() => {
    setPhotos(initialPhotos);
    // Only re-hydrate when switching birds — avoid loops from new [] references each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [birdId]);

  const updatePhotos = (next: Photo[] | ((prev: Photo[]) => Photo[])) => {
    setPhotos((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      onPhotosChange?.(resolved);
      return resolved;
    });
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('birdId', birdId);

    try {
      const uploadedPhoto = await uploadBirdPhoto(formData);
      updatePhotos((prev) => [...prev, uploadedPhoto]);
    } catch (error) {
      console.error('Failed to upload photo:', error);
      throw error;
    }
  };

  const handleDelete = async (photoId: string) => {
    try {
      await deletePhoto(photoId);
      updatePhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (error) {
      console.error('Failed to delete photo:', error);
      throw error;
    }
  };

  const handleCaptionUpdate = async (photoId: string, caption: string) => {
    try {
      const updatedPhoto = await updatePhotoCaption(photoId, caption);
      updatePhotos((prev) =>
        prev.map((p) => (p.id === photoId ? updatedPhoto : p))
      );
    } catch (error) {
      console.error('Failed to update caption:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <PhotoUpload onUpload={handleUpload} />
      <PhotoGallery
        photos={photos}
        onPhotoDelete={handleDelete}
        onCaptionUpdate={handleCaptionUpdate}
        onAvatarSelect={onAvatarChange}
        selectedAvatar={selectedAvatar}
      />
    </div>
  );
}
